/* eslint-disable react/jsx-props-no-spreading */
/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import EmotionReactModal from "components/EmotionReactModal";
import useCreateScheduledReportMutation from "hookMutation/useCreateScheduledReportMutation";
import useUpdateScheduledReportMutation from "hookMutation/useUpdateScheduledReportMutation";
import usePreviewSqlMutation from "hookMutation/usePreviewSqlMutation";
import useEvent from "hooks/useEvent";
import parseDate from "helpers/parseDateInLocalTime";
import ModalHeader from "components/common/ModalHeader";
import { Controller, useForm } from "react-hook-form";
import Input from "components/common/Input";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "components/common/Select";
import { mTop40Css } from "assets/css/margins.css";
import validationSchema from "./validation";
import {
  buttonCss,
  leftContainerCss,
  modalContainerCss,
  modalContentCss,
  modalCss,
  overlayCss,
  rightContainerCss,
  sectionCss,
  tabsContentCss,
  titleCss,
} from "./index.css";
import TextArea from "../partner/EditPartnerForm/TextArea";
import DatePickerInput from "../dailyOutreach/DueDate/DatePickerInput";
import { gap16Css } from "../profile/EditForm/index.css";

const FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];
const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const EMPTY_FORM_STATE = {
  function_name: "",
  subject: "",
  recipients: "",
  frequency: null,
  startingOn: null,
  sql: "",
  status: STATUS_OPTIONS.find((o) => o.value === "active"),
};

export default function ScheduledReportsFormModal({ isOpen, onClose, report }) {
  const [, setIsCalendarOpen] = useState(false);
  const onCalendarClose = useEvent(() => setIsCalendarOpen(false));
  const onCalendarOpen = useEvent(() => setIsCalendarOpen(true));
  const [previewData, setPreviewData] = useState(null);
  const isEditing = !!report;

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isValid, isDirty },
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: EMPTY_FORM_STATE,
    mode: "onChange",
  });

  const createMutation = useCreateScheduledReportMutation();
  const updateMutation = useUpdateScheduledReportMutation();
  const previewSqlMutation = usePreviewSqlMutation();

  useEffect(() => {
    if (!isOpen) setPreviewData(null);
  }, [isOpen]);

  useEffect(() => {
    if (isEditing && report) {
      reset({
        function_name: report.name,
        subject: report.subject,
        recipients: report.recipients?.join(", ") || "",
        frequency: FREQUENCY_OPTIONS.find((o) => o.value === report.frequency) || null,
        status: STATUS_OPTIONS.find((o) => o.value === report.status) || STATUS_OPTIONS[0],
        sql: report.sql,
        startingOn: report.startingOn ? new Date(report.startingOn) : null,
      });
    } else if (!isEditing) {
      reset(EMPTY_FORM_STATE);
    }
  }, [report, isEditing, reset, isOpen]);

  const onPreview = () => {
    const currentSql = getValues("sql");
    if (!currentSql) return;
    previewSqlMutation.mutate(
      { sql: currentSql },
      {
        onSuccess: (data) => setPreviewData(data),
      },
    );
  };
  const onSubmit = (formData) => {
    const scheduledReportsData = {
      name: formData.function_name,
      functionName: formData.function_name,
      subject: formData.subject,
      frequency: formData.frequency.value,
      status: formData.status.value,
      sql: formData.sql,
      startingOn: formData.startingOn ? new Date(formData.startingOn).toISOString() : null,
      recipients: formData.recipients
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };

    if (isEditing) {
      updateMutation.mutate(
        { reportId: report.id, body: scheduledReportsData },
        { onSuccess: onClose },
      );
      trigger();
    } else {
      createMutation.mutate({ body: scheduledReportsData }, { onSuccess: onClose });
    }
  };
  const isSubmitDisabled =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    !isValid ||
    (isEditing ? false : !isDirty);

  const currentSql = getValues("sql");
  const isPreviewDisabled = previewSqlMutation.isLoading || !currentSql;
  return (
    <EmotionReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={modalCss}
      overlayClassName={overlayCss}
    >
      <form onSubmit={handleSubmit(onSubmit)} css={[modalContainerCss]}>
        <div css={sectionCss}>
          <ModalHeader handleClose={onClose}>
            <span css={titleCss}>
              {isEditing ? "Edit Scheduled Report" : "Create Scheduled Report"}
            </span>
          </ModalHeader>
        </div>
        <div className="flex" css={[tabsContentCss, modalContentCss]}>
          <div css={leftContainerCss}>
            <Input
              label="Report Name"
              error={errors.function_name?.message}
              {...register("function_name")}
            />
            <Input
              label="Email Subject Line"
              error={errors.subject?.message}
              {...register("subject")}
            />
            <TextArea
              label="Recipients (comma separated)"
              placeholder="admin@company.com, ceo@company.com"
              name="recipients"
              errorMessage={errors.recipients?.message}
              register={register}
            />

            <Controller
              name="frequency"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={FREQUENCY_OPTIONS}
                  label="Frequency"
                  variant="outlined"
                  error={errors.frequency?.message}
                />
              )}
            />
          </div>

          <div css={rightContainerCss}>
            <Controller
              name="startingOn"
              control={control}
              render={({ field }) => (
                <DatePickerInput
                  label="Starting On"
                  placeholderText="Select date"
                  onChange={(date) => field.onChange(parseDate(date))}
                  onCalendarClose={onCalendarClose}
                  onCalendarOpen={onCalendarOpen}
                  selected={field.value}
                  autoComplete="off"
                  dateFormat="dd MMM yy"
                  minDate={parseDate(Date.now())}
                />
              )}
            />
            <TextArea
              label="SQL"
              placeholder="SELECT p.first_name, c.name FROM prospect p JOIN ..."
              name="sql"
              errorMessage={errors.sql?.message}
              register={register}
            />
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={STATUS_OPTIONS}
                  label="Status"
                  variant="outlined"
                  error={errors.status?.message}
                />
              )}
            />
          </div>
        </div>
        <div css={gap16Css} className="flex-a-c">
          <button type="submit" css={[buttonCss, mTop40Css]} disabled={isSubmitDisabled}>
            Save
          </button>
          <button
            type="button"
            css={[buttonCss, mTop40Css]}
            disabled={isPreviewDisabled}
            onClick={onPreview}
          >
            Preview
          </button>
        </div>
        \{/* --- PREVIEW TABLE SECTION --- */}
        {previewData && (
          <div css={{ padding: "0 20px", marginTop: "20px", width: "100%" }}>
            <span css={[titleCss, { fontSize: "14px", marginBottom: "8px", display: "block" }]}>
              Data Preview (Top 10)
            </span>
            <div
              css={{
                maxHeight: "400px",
                overflowX: "auto",
                overflowY: "auto",
                border: "1px solid #eee",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <table
                css={{
                  minWidth: "1000px",
                  width: "max-content",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  textAlign: "left",
                  tableLayout: "auto",
                }}
              >
                <thead
                  css={{
                    position: "sticky",
                    top: 0,
                    background: "#f9f9f9",
                    zIndex: 1,
                    boxShadow: "0 1px 0 #eee",
                  }}
                >
                  <tr>
                    {Object.keys(previewData[0] || {}).map((key) => (
                      <th
                        key={key}
                        css={{
                          padding: "10px",
                          borderBottom: "1px solid #eee",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={i} css={{ borderBottom: "1px solid #eee" }}>
                      {Object.values(row).map((val, j) => (
                        <td
                          // eslint-disable-next-line react/no-array-index-key
                          key={j}
                          css={{
                            padding: "10px",
                            whiteSpace: "nowrap",
                            maxWidth: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </form>
    </EmotionReactModal>
  );
}

ScheduledReportsFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    frequency: PropTypes.string,
    startingOn: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    nextScheduledRun: PropTypes.string,
    functionName: PropTypes.string,
    recipients: PropTypes.arrayOf(PropTypes.string),
    subject: PropTypes.string,
    sql: PropTypes.string,
    status: PropTypes.string,
  }),
};
